import { make } from '@/editorjs/utils';

export default class BlockStyleTune {
    /**
     * Tune의 정적 getter는 Tune 메뉴에 아이콘과 제목을 표시하기 위해 필요합니다.
     */
    static get isTune() {
        return true;
    }

    /**
     * @param {{api: object, data: object, config: object, block: object}}
     */
    constructor({ api, data = {}, block }) {
        this.api = api;
        this.block = block;

        this.fourSidedProps = ['padding', 'margin', 'borderRadius', 'borderWidth'];
        this.borderProps = ['borderWidth', 'borderStyle', 'borderColor'];

        this.data = this.migrateData(data); // 데이터 구조 초기화/마이그레이션

        // 테두리 관련 설정
        this.BORDER_DEFAULTS = { width: '1', style: 'solid', color: '#000000' };
        this.BORDER_STYLE_OPTIONS = {
            '': '선택...', 'solid': '실선', 'dashed': '파선', 'dotted': '점선',
            'double': '이중선', 'groove': '입체 (오목)',
        };

        this.wrapper = undefined;
        this.blockWrapper = undefined;
        this.inputs = {}; // DOM 요소 참조를 저장할 객체
    }

    /**
     * ✨ 2. 데이터 구조를 현대화하고, 이전 버전 데이터(단일 값)를 호환합니다.
     * e.g.) 'padding: "10"' -> 'padding: { top: "10", right: "10", bottom: "10", left: "10" }'
     */
    migrateData(data) {
        const newData = {};

        this.fourSidedProps.forEach(prop => {
            // borderRadius의 경우, 이전 키(top, right 등)를 새로운 키(topLeft 등)로 매핑합니다.
            if (prop === 'borderRadius' && typeof data[prop] === 'object' && data[prop] !== null) {
                newData[prop] = {
                    topLeft: data[prop].topLeft || data[prop].top || '',
                    topRight: data[prop].topRight || data[prop].right || '',
                    bottomRight: data[prop].bottomRight || data[prop].bottom || '',
                    bottomLeft: data[prop].bottomLeft || data[prop].left || '',
                };
            } else if (typeof data[prop] === 'object' && data[prop] !== null) {
                newData[prop] = data[prop];
            } else if (data[prop]) {
                const value = data[prop];
                // borderRadius는 새로운 키 구조로 변환
                if (prop === 'borderRadius') {
                    newData[prop] = { topLeft: value, topRight: value, bottomRight: value, bottomLeft: value };
                } else {
                    newData[prop] = { top: value, right: value, bottom: value, left: value };
                }
            } else {
                newData[prop] = {};
            }
        });

        Object.keys(data).forEach(key => {
            if (!this.fourSidedProps.includes(key)) {
                newData[key] = data[key];
            }
        });

        return newData;
    }


    /**
     * 블록을 감싸는 wrapper를 만들고 스타일을 적용합니다.
     */
    wrap(blockContent) {
        // 글박스 등에서 설정한 기본 스타일 확인 및 적용
        this._applyDefaultStylesIfNeeded(blockContent);

        const wrapper = make('div');
        // 외부 도구(Columns 초기 설정 뷰 등)가 이 tune 인스턴스에 접근할 때 사용하는 legacy 참조
        wrapper._blockStyleTune = this;
        // collab remote apply가 dispatch하는 tune 등록명(camelCase, editor_core.js tools 키) 마커
        wrapper.dataset.tuneName = 'blockStyle';
        wrapper._tuneInstance = this;
        this.applyStyle(wrapper);
        wrapper.appendChild(blockContent);
        this.blockWrapper = wrapper;
        return wrapper;
    }

    /**
     * 외부 도구에서 스타일을 병합 적용
     * (Columns 초기 설정 뷰 등에서 사용 — wrap 이후 시점에 데이터 주입)
     *
     * 4면 속성(padding/margin/borderRadius/borderWidth)은 단일 값(예: "10")을 받으면
     * migrateData 를 통해 4면 객체({top, right, bottom, left})로 자동 확장된다.
     */
    applyExternalStyles(stylesData) {
        if (!stylesData || typeof stylesData !== 'object') return;

        const merged = { ...this.data };
        Object.keys(stylesData).forEach(key => {
            const value = stylesData[key];
            if (value === '' || value === null || value === undefined) {
                delete merged[key];
            } else {
                merged[key] = value;
            }
        });

        // migrateData 가 단일 값을 4면 객체로 확장하고 데이터 구조를 정규화
        this.data = this.migrateData(merged);

        // 테두리 속성이 부분적으로만 설정된 경우 누락된 기본값 채우기
        // (예: borderColor 만 있고 borderWidth/borderStyle 없음)
        const borderKeys = ['borderWidth', 'borderStyle', 'borderColor'];
        if (borderKeys.some(k => stylesData[k])) {
            // 임의의 한 키로 syncBorderProperties 트리거
            const triggerKey = borderKeys.find(k => stylesData[k]);
            this.syncBorderProperties(triggerKey, this.data[triggerKey]);
        }

        this.updateAndSave();
    }

    /**
     * 툴에서 설정한 기본 스타일이 있으면 적용
     * (Columns의 글박스 모드 등에서 사용)
     */
    _applyDefaultStylesIfNeeded(blockContent) {
        // 이미 스타일이 있으면 적용하지 않음
        const hasExistingStyles = Object.keys(this.data).some(key => {
            const value = this.data[key];
            if (typeof value === 'object' && value !== null) {
                return Object.values(value).some(v => v);
            }
            return !!value;
        });

        if (hasExistingStyles) return;

        // blockContent 또는 자식 요소에서 data-textbox-styles 속성을 가진 요소 찾기
        try {
            // blockContent 자체 또는 내부에서 ce-columns 찾기
            let columnsElement = null;

            if (blockContent?.classList?.contains('ce-columns')) {
                columnsElement = blockContent;
            } else if (blockContent?.querySelector) {
                columnsElement = blockContent.querySelector('.ce-columns[data-textbox-styles]');
            }

            if (columnsElement) {
                const defaultStylesJson = columnsElement.dataset?.textboxStyles;
                if (defaultStylesJson) {
                    const defaultStyles = JSON.parse(defaultStylesJson);
                    this.data = this.migrateData(defaultStyles);
                    // 속성 제거 (더 이상 필요 없음)
                    delete columnsElement.dataset.textboxStyles;
                }
            }
        } catch (e) {
            console.debug('[BlockStyleTune] Error applying default styles:', e);
        }
    }

    render() {
        return [{
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z" /><path d="M12 8l0 .01" /><path d="M8 12l0 .01" /><path d="M12 12l0 .01" /><path d="M16 12l0 .01" /><path d="M12 16l0 .01" /></svg>`,
            title: '블록 스타일',
            children: {
                items: this.createStyleSettingItems()
            },
            closeOnActivate: false,
        }, {
            type: "separator"
        }];
    }

    /**
     * 스타일 설정 메뉴 아이템들을 생성합니다.
     */
    createStyleSettingItems() {
        const settings = [
            { key: 'padding', type: 'four-sided', unit: 'px', label: '패딩' },
            { key: 'margin', type: 'four-sided', unit: 'px', label: '마진' },
            { key: 'maxWidth', type: 'number', unit: 'px', label: '최대 너비', hint: '설정 시 가운데 정렬' },
            { type: 'separator' },
            { key: 'borderRadius', type: 'four-sided', unit: 'px', label: '모서리 둥글기' },
            { key: 'borderWidth', type: 'four-sided', unit: 'px', label: '테두리 두께' },
            { key: 'borderStyle', type: 'select', label: '테두리 스타일', options: this.BORDER_STYLE_OPTIONS },
            { key: 'borderColor', type: 'color', label: '테두리 색', hint: '다크모드 자동 반전' },
            { type: 'separator' },
            { key: 'textColor', type: 'color', label: '글자색' },
            { key: 'backgroundColor', type: 'color', label: '배경색', hint: '다크모드 자동 반전' },
            { type: 'separator' },
            { type: 'reset', label: '스타일 초기화' }
        ];

        return settings.map(setting => this.createSettingItem(setting));
    }

    /**
     * 설정 타입에 따라 적절한 입력 요소를 생성하는 래퍼 함수입니다.
     */
    createSettingItem(setting) {
        if (setting.type === 'separator') return { type: 'separator' };
        if (setting.type === 'reset') return this.createResetButton(setting);
        if (setting.type === 'info') return this.createInfoItem(setting);

        let element;
        const commonEventHandler = (key, value) => this.handleValueChange(key, value);

        switch (setting.type) {
            case 'four-sided':
                element = this.createFourSidedInput(setting.key, setting.label, setting.unit);
                break;
            case 'color':
                element = this.createColorInput(setting.key, setting.label, commonEventHandler, setting.hint);
                break;
            case 'select':
                element = this.createSelectInput(setting.key, setting.label, setting.options, commonEventHandler);
                break;
            case 'number':
                element = this.createNumberInput(setting.key, setting.label, setting.unit, setting.hint, commonEventHandler);
                break;
            // 다른 타입이 필요하면 여기에 추가
        }

        return { type: 'html', element, title: setting.label };
    }

    createFourSidedInput(key, label, unit) {
        this.inputs[key] = {}; // 해당 속성의 input 참조를 저장할 객체 초기화
        const data = this.data[key] || {};
        const isBorderRadius = key === 'borderRadius';
        const sides = isBorderRadius
            ? ['topLeft', 'topRight', 'bottomRight', 'bottomLeft']
            : ['top', 'right', 'bottom', 'left'];

        const icons = {
            // 일반 4방향 아이콘
            top: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M4.182 6.036L7.5 2.718l3.318 3.318H4.182z" fill="currentColor"></path></svg>`,
            right: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M8.964 4.182L12.282 7.5 8.964 10.818V4.182z" fill="currentColor"></path></svg>`,
            bottom: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M10.818 8.964L7.5 12.282 4.182 8.964h6.636z" fill="currentColor"></path></svg>`,
            left: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M6.036 10.818L2.718 7.5 6.036 4.182v6.636z" fill="currentColor"></path></svg>`,
            // borderRadius 전용 아이콘
            topLeft: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M7 1C3.686 1 1 3.686 1 7V1H7Z" fill="currentColor"/></svg>`,
            topRight: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M8 1H14V7C14 3.686 11.314 1 8 1Z" fill="currentColor"/></svg>`,
            bottomRight: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M8 14C11.314 14 14 11.314 14 8V14H8Z" fill="currentColor"/></svg>`,
            bottomLeft: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M7 14H1V8C1 11.314 3.686 14 7 14Z" fill="currentColor"/></svg>`,
        };

        // 전체 컨테이너
        const container = make('div', 'maily-editor-utils--four-sided-container');
        const labelEl = make('label', 'maily-editor-utils--four-sided-label', { textContent: `${label} (${unit})` });

        // 토글 버튼 + 입력 필드를 감싸는 래퍼
        const contentWrapper = make('div', 'maily-editor-utils--four-sided-content-wrapper');

        // 단일 입력 필드 (이제 wrapper 없이 바로 생성)
        const singleInput = make('input', ['maily-editor-utils--input-default', 'w-full'], {
            type: 'number',
            min: 0,
            placeholder: '전체',
        });
        this.inputs[key].single = singleInput;

        singleInput.addEventListener('input', (e) => {
            const value = e.target.value;
            const newValues = {};
            sides.forEach(side => {
                newValues[side] = value; // 올바른 키 이름으로 할당
                if (this.inputs[key][side]) this.inputs[key][side].value = value;
            });
            this.handleValueChange(key, newValues);
        });

        // 개별 입력 필드(2x2 그리드)를 담는 컨테이너
        const detailsContainer = make('div', 'maily-editor-utils--four-sided-details');
        sides.forEach(side => {
            const inputId = `maily-style-tune-${key}-${side}-${this.block.id}`;
            const sideInputWrapper = make('div', 'maily-editor-utils--four-sided-side-input-wrapper');
            const iconLabel = make('label', 'maily-editor-utils--four-sided-side-icon', {
                innerHTML: icons[side],
                htmlFor: inputId, // input의 id와 연결
            });
            const sideInput = make('input', 'maily-editor-utils--four-sided-side-input', {
                type: 'number',
                min: 0,
                placeholder: '-',
                value: data[side] || '',
                id: inputId
            });

            this.inputs[key][side] = sideInput;

            sideInput.addEventListener('input', (e) => {
                // 현재 데이터 복사 후 변경된 값만 업데이트
                const newValues = { ...(this.data[key] || {}) };
                newValues[side] = e.target.value;

                singleInput.value = ''; // 개별 입력 시 단일 입력 필드 비움

                // 범용 핸들러 호출하여 데이터 변경 및 테두리 동기화 로직 실행
                this.handleValueChange(key, newValues);
            });

            sideInputWrapper.append(iconLabel, sideInput);
            detailsContainer.appendChild(sideInputWrapper);
        });

        // 펼치기/접기 버튼
        const toggleIcon = isBorderRadius
            ? `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v2" /><path d="M20 16v2a2 2 0 0 1 -2 2h-2" /><path d="M8 20h-2a2 2 0 0 1 -2 -2v-2" /><path d="M4 8v-2a2 2 0 0 1 2 -2h2" /></svg>`
            : `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8v8" /><path d="M20 16v-8" /><path d="M8 4h8" /><path d="M8 20h8" /></svg>`;

        const toggleButton = make('button', 'maily-editor-utils--four-sided-toggle-button', {
            innerHTML: toggleIcon,
            type: 'button',
        });


        // 초기 상태 결정
        const isUniform = sides.every(s => data[s] && data[s] === data[sides[0]]);
        let isExpanded = !isUniform && Object.values(data).some(v => v);

        const setVisibility = () => {
            detailsContainer.style.display = isExpanded ? 'grid' : 'none';
            singleInput.style.display = isExpanded ? 'none' : 'block';
        };

        if (isUniform && data[sides[0]]) {
            singleInput.value = data[sides[0]];
        }

        setVisibility(); // 초기 가시성 설정

        toggleButton.addEventListener('click', () => {
            isExpanded = !isExpanded;
            setVisibility();
            let newValues = { ...(this.data[key] || {}) };
            if (isExpanded) {
                const singleValue = singleInput.value;
                if (singleValue) {
                    sides.forEach(side => newValues[side] = singleValue);
                }
            } else {
                // ✨ 접을 때는 첫 번째 키(topLeft 또는 top)를 기준으로 통일
                const firstValue = this.inputs[key][sides[0]].value;
                if (firstValue) {
                    sides.forEach(side => newValues[side] = firstValue);
                }
            }
            this.handleValueChange(key, newValues);
        });

        contentWrapper.append(singleInput, detailsContainer, toggleButton);
        container.append(labelEl, contentWrapper);

        return container;
    }

    createColorInput(key, label, eventHandler, hint) {
        const container = make('div', 'maily-editor-utils--input-container');
        const labelEl = make('label', 'maily-editor-utils--input-label', { textContent: label });

        if (hint) {
            const hintEl = make('span', 'maily-editor-utils--input-hint', { textContent: hint });
            labelEl.appendChild(hintEl);
        }

        const inputWrapper = make('div', 'flex items-center w-full');

        const textInput = make('input', 'maily-editor-utils--input-color-text', {
            type: 'text',
            value: this.data[key] || '',
            placeholder: '#000000',
        });
        const colorInput = make('input', 'maily-editor-utils--input-color-picker', {
            type: 'color',
            value: this.data[key] || '#ffffff',
        });

        if (this.borderProps.includes(key)) {
            this.inputs[key] = { text: textInput, color: colorInput };
        }

        textInput.addEventListener('input', (e) => {
            colorInput.value = e.target.value || '#ffffff';
            eventHandler(key, e.target.value);
        });
        colorInput.addEventListener('input', (e) => {
            textInput.value = e.target.value;
            eventHandler(key, e.target.value);
        });

        inputWrapper.append(textInput, colorInput);
        container.append(labelEl, inputWrapper);
        return container;
    }

    createSelectInput(key, label, options, eventHandler) {
        const container = make('div', 'maily-editor-utils--input-container');
        const labelEl = make('label', 'maily-editor-utils--input-label', { textContent: label });
        const selectEl = make('select', 'maily-editor-utils--input-default');

        if (this.borderProps.includes(key)) {
            this.inputs[key] = { select: selectEl };
        }

        Object.entries(options).forEach(([value, text]) => {
            const optionEl = make('option', null, { value, textContent: text });
            if (this.data[key] === value) optionEl.selected = true;
            selectEl.appendChild(optionEl);
        });

        selectEl.addEventListener('change', (e) => eventHandler(key, e.target.value));

        container.append(labelEl, selectEl);
        return container;
    }

    /**
     * 단일 숫자 입력 필드 생성 (max-width 등)
     * type=text + inputmode=numeric 를 사용해 EditorJS caret 의 selectionEnd 호환 문제 회피
     */
    createNumberInput(key, label, unit, hint, eventHandler) {
        const container = make('div', 'maily-editor-utils--input-container');
        const labelEl = make('label', 'maily-editor-utils--input-label', { textContent: `${label} (${unit})` });
        if (hint) {
            const hintEl = make('span', 'maily-editor-utils--input-hint', { textContent: hint });
            labelEl.appendChild(hintEl);
        }
        const inputEl = make('input', 'maily-editor-utils--input-default', {
            type: 'text',
            inputmode: 'numeric',
            pattern: '[0-9]*',
            placeholder: '비움',
            value: this.data[key] || '',
        });
        this.inputs[key] = { single: inputEl };

        inputEl.addEventListener('input', (e) => {
            const cleaned = e.target.value.replace(/[^0-9]/g, '');
            if (cleaned !== e.target.value) e.target.value = cleaned;
            eventHandler(key, cleaned);
        });

        container.append(labelEl, inputEl);
        return container;
    }

    createResetButton(setting) {
        return {
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7l16 0" /><path d="M10 11l0 6" /><path d="M14 11l0 6" /><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" /><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" /></svg>`,
            title: setting.label,
            hint: { title: "이 블록에 설정 된 블록 스타일이 전부 초기화 됩니다." },
            closeOnActivate: true,
            onActivate: () => this.resetStyle(),
        };
    }

    createInfoItem(setting) {
        const element = make('div', 'maily-editor-utils--info-text', { textContent: setting.text });
        return { type: 'html', element, title: '' };
    }

    handleValueChange(key, value) {
        this.data[key] = value;

        if (this.borderProps.includes(key)) {
            // ✨ 변경된 키와 값을 함께 전달하여 "삭제" 의도를 파악할 수 있게 함
            this.syncBorderProperties(key, value);
        }

        this.updateAndSave();
    }

    syncBorderProperties(changedKey, changedValue) {
        // 1. 사용자의 액션이 "삭제"인지 명확하게 판단합니다.
        // - 값이 없거나 ('' 등)
        // - 객체인데 모든 내부 값이 비어있는 경우
        const isDeletion = !changedValue ||
            (typeof changedValue === 'object' && Object.values(changedValue).every(v => !v));

        if (isDeletion) {
            // 2. "삭제"가 맞다면, 다른 것을 확인하지 않고 즉시 모든 테두리 데이터를 비웁니다.
            this.data.borderWidth = {};
            this.data.borderStyle = '';
            this.data.borderColor = '';

            // 3. UI를 업데이트하고 함수를 즉시 종료합니다. (가장 중요)
            this.updateBorderInputsUI();
            return;
        }

        // 4. "삭제"가 아니라면, "추가/변경" 로직을 수행합니다.
        //    (이 부분은 기존 로직과 동일)
        const hasBorderWidth = this.data.borderWidth && Object.values(this.data.borderWidth).some(v => v);
        const hasBorderStyle = !!this.data.borderStyle;
        const hasBorderColor = !!this.data.borderColor;

        // 현재 어떤 값이든 하나라도 존재하면, 나머지를 기본값으로 채웁니다.
        if (hasBorderWidth || hasBorderStyle || hasBorderColor) {
            if (!hasBorderStyle) {
                this.data.borderStyle = this.BORDER_DEFAULTS.style;
            }
            if (!hasBorderColor) {
                this.data.borderColor = this.BORDER_DEFAULTS.color;
            }
            if (!hasBorderWidth) {
                const defaultWidth = this.BORDER_DEFAULTS.width;
                this.data.borderWidth = { top: defaultWidth, right: defaultWidth, bottom: defaultWidth, left: defaultWidth };
            }
        }

        // 5. 최종적으로 결정된 데이터 상태를 UI에 반영합니다.
        this.updateBorderInputsUI();
    }

    updateBorderInputsUI() {
        // borderWidth 입력 필드 업데이트
        if (this.inputs.borderWidth) {
            const bwData = this.data.borderWidth || {};
            const sides = ['top', 'right', 'bottom', 'left'];

            // 1. 개별 입력창은 항상 데이터에 따라 업데이트
            sides.forEach(side => {
                if (this.inputs.borderWidth[side]) {
                    this.inputs.borderWidth[side].value = bwData[side] || '';
                }
            });

            // 2. 단일 입력창 상태 결정
            const isUniform = sides.every(s => bwData[s] && bwData[s] === bwData.top);
            const isEmpty = Object.values(bwData).every(v => !v);

            if (isEmpty) {
                // ✨ 데이터가 완전히 비었으면, 단일 입력창도 비운다. (가장 중요)
                this.inputs.borderWidth.single.value = '';
            } else if (isUniform) {
                // 모든 값이 동일하면, 단일 입력창에 그 값을 표시
                this.inputs.borderWidth.single.value = bwData.top;
            } else {
                // 값이 다르지만 비어있지는 않으면, 단일 입력창을 비워둠 (개별 입력 중이라는 의미)
                this.inputs.borderWidth.single.value = '';
            }
        }

        // borderStyle select 요소 업데이트
        if (this.inputs.borderStyle && this.inputs.borderStyle.select) {
            this.inputs.borderStyle.select.value = this.data.borderStyle || '';
        }

        // borderColor 입력 필드 업데이트
        if (this.inputs.borderColor && this.inputs.borderColor.text) {
            this.inputs.borderColor.text.value = this.data.borderColor || '';
            this.inputs.borderColor.color.value = this.data.borderColor || '#ffffff';
        }

        // ✨ borderRadius 입력 필드 업데이트
        if (this.inputs.borderRadius) {
            const brData = this.data.borderRadius || {};
            const sides = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];

            // 1. 개별 입력창 업데이트
            sides.forEach(side => {
                if (this.inputs.borderRadius[side]) {
                    this.inputs.borderRadius[side].value = brData[side] || '';
                }
            });

            // 2. 단일 입력창 상태 결정 (borderWidth와 동일한 로직 적용)
            const isUniform = sides.every(s => brData[s] && brData[s] === brData.topLeft);
            const isEmpty = Object.values(brData).every(v => !v);

            if (isEmpty) {
                this.inputs.borderRadius.single.value = '';
            } else if (isUniform) {
                this.inputs.borderRadius.single.value = brData.topLeft;
            } else {
                this.inputs.borderRadius.single.value = '';
            }
        }
    }


    updateAndSave() {
        this.updateBlockStyle();
        this.block.save();
        // collab: tune 변경은 DOM style(attribute)만 바꾸므로 MutationObserver와
        // onBlockChange 어느 쪽도 안정적으로 발화하지 못한다. YEditorBinding을 redactor에서
        // 직접 찾아 로컬 sync를 예약한다.
        this._requestCollabSync();
    }

    /**
     * YEditorBinding을 `_yBinding` 속성을 가진 가장 가까운 조상에서 찾아 로컬 sync를 예약한다.
     * closest('.codex-editor__redactor')를 쓰지 않는 이유: columns 등 nested editor 내부 블록은
     * nested redactor(binding 없음)를 먼저 찾아버려 no-op 되는 버그를 회피.
     */
    _requestCollabSync() {
        try {
            let el = this.blockWrapper;
            while (el && !el._yBinding) el = el.parentElement;
            const binding = el?._yBinding;
            if (binding && typeof binding.requestLocalSync === 'function') {
                binding.requestLocalSync();
            }
        } catch { /* noop */ }
    }

    /**
     * 원격(collab)에서 수신한 tune 데이터를 전체 치환하고 DOM에 반영.
     * dispatchChange 호출 금지 — remote apply 루프 방지.
     *
     * @param {object|null} remoteData - Y.Doc에서 전달된 tunes['block-style'] 값 (없으면 null)
     * @returns {boolean} 실제 변경 여부
     */
    applyRemoteData(remoteData) {
        const next = (remoteData && typeof remoteData === 'object')
            ? this.migrateData({ ...remoteData })
            : this.migrateData({});

        // 동일하면 skip
        if (JSON.stringify(this.data) === JSON.stringify(next)) return false;

        this.data = next;
        this.updateBlockStyle();
        return true;
    }

    resetStyle() {
        // 모든 데이터 초기화
        this.data = this.migrateData({}); // 빈 객체로 초기화

        // DOM 입력 필드 초기화
        Object.values(this.inputs).forEach(propInputs => {
            Object.values(propInputs).forEach(input => {
                if (input) input.value = '';
            });
        });

        this.updateAndSave();
    }

    updateBlockStyle() {
        if (this.blockWrapper) {
            this.applyStyle(this.blockWrapper);
        }
    }

    /**
     * ✨ 4. 새로운 데이터 구조를 기반으로 실제 CSS 스타일을 적용합니다.
     */
    applyStyle(element) {
        if (!element) return;

        // 4면 제어 속성 적용
        this.fourSidedProps.forEach(prop => {
            const values = this.data[prop];
            if (values && Object.values(values).some(v => v)) {
                if (prop === 'borderRadius') {
                    // borderRadius는 개별 속성으로 적용
                    element.style.borderTopLeftRadius = values.topLeft ? `${values.topLeft}px` : '';
                    element.style.borderTopRightRadius = values.topRight ? `${values.topRight}px` : '';
                    element.style.borderBottomRightRadius = values.bottomRight ? `${values.bottomRight}px` : '';
                    element.style.borderBottomLeftRadius = values.bottomLeft ? `${values.bottomLeft}px` : '';
                } else if (prop === 'borderWidth') {
                    // borderWidth도 개별 속성으로 적용
                    element.style.borderTopWidth = values.top ? `${values.top}px` : '';
                    element.style.borderRightWidth = values.right ? `${values.right}px` : '';
                    element.style.borderBottomWidth = values.bottom ? `${values.bottom}px` : '';
                    element.style.borderLeftWidth = values.left ? `${values.left}px` : '';
                } else {
                    // padding, margin은 단축 속성으로 적용
                    element.style[prop] = ['top', 'right', 'bottom', 'left']
                        .map(side => `${values[side] || 0}px`)
                        .join(' ');
                }
            } else if (values) {
                // 값이 모두 비어있으면 스타일 초기화
                if (prop === 'borderRadius') {
                    element.style.borderRadius = '';
                } else if (prop === 'borderWidth') {
                    element.style.borderWidth = '';
                } else {
                    element.style[prop] = '';
                }
            }
        });

        // 최대 너비 + 가운데 정렬 (margin shorthand 적용 이후에 marginLeft/Right 를 override)
        if (this.data.maxWidth) {
            element.style.maxWidth = `${this.data.maxWidth}px`;
            element.style.marginLeft = 'auto';
            element.style.marginRight = 'auto';
        } else {
            element.style.maxWidth = '';
            // marginLeft/Right 는 위 fourSidedProps margin 처리에서 이미 정리됨
        }

        // 기타 스타일 적용
        element.style.color = this.data.textColor || '';
        element.style.backgroundColor = this.data.backgroundColor || '';

        // 테두리 스타일 및 색상 적용 (두께는 위에서 이미 처리됨)
        const { borderStyle, borderColor } = this.data;
        const hasAnyBorderValue = borderStyle || borderColor || Object.keys(this.data.borderWidth || {}).some(k => this.data.borderWidth[k]);

        if (hasAnyBorderValue) {
            element.style.borderStyle = borderStyle || this.BORDER_DEFAULTS.style;
            element.style.borderColor = borderColor || this.BORDER_DEFAULTS.color;
        } else {
            element.style.borderStyle = '';
            element.style.borderColor = '';
        }

        // box-sizing은 패딩이나 테두리가 있을 때 항상 필요
        const hasPadding = this.data.padding && Object.values(this.data.padding).some(v => v);
        if (hasPadding || hasAnyBorderValue) {
            element.style.boxSizing = 'border-box';
        } else {
            element.style.boxSizing = '';
        }
        // 주의: overflow: hidden은 borderRadius가 있어도 적용하지 않음
        // - 배경색은 border-radius만으로 자연스럽게 라운딩됨
        // - overflow: hidden은 에디터 내부의 팝오버/툴바를 가리는 문제를 일으킴
        // - 이미지 등 특정 콘텐츠의 클리핑이 필요한 경우 해당 도구에서 자체 처리
        element.style.overflow = '';

        // 다크모드 색상 반전을 위한 마커 클래스 (textColor는 제외)
        if (this.data.backgroundColor || this.data.borderColor) {
            element.classList.add('has-block-style-color');
        } else {
            element.classList.remove('has-block-style-color');
        }
    }

    save() {
        const dataToSave = { ...this.data };

        // 4면 제어 속성 최적화 (이전과 동일)
        this.fourSidedProps.forEach(prop => {
            const values = dataToSave[prop];
            if (typeof values === 'object' && values !== null) {
                const sides = prop === 'borderRadius'
                    ? ['topLeft', 'topRight', 'bottomRight', 'bottomLeft']
                    : ['top', 'right', 'bottom', 'left'];

                if (sides.every(s => !values[s])) {
                    delete dataToSave[prop];
                    return;
                }

                if (sides.every(s => values[s] && values[s] === values[sides[0]])) {
                    dataToSave[prop] = values[sides[0]]; // 모든 값이 같으면 단일 값으로 저장
                }
            }
        });

        // 테두리 데이터 정리 로직 강화
        const { borderWidth, borderStyle, borderColor } = dataToSave;
        // 저장 시점의 borderWidth 값 존재 여부 확인 (단일값 또는 객체값)
        const hasBorderWidth = borderWidth && (typeof borderWidth === 'string' || Object.values(borderWidth).some(v => v));
        const hasAnyBorderValue = hasBorderWidth || borderStyle || borderColor;

        if (hasAnyBorderValue) {
            // 값이 하나라도 있으면, 없는 값은 기본값으로 채워서 저장
            if (!dataToSave.borderStyle) dataToSave.borderStyle = this.BORDER_DEFAULTS.style;
            if (!dataToSave.borderColor) dataToSave.borderColor = this.BORDER_DEFAULTS.color;
            // borderWidth는 단일값 기본값으로 저장
            if (!hasBorderWidth) dataToSave.borderWidth = this.BORDER_DEFAULTS.width;
        } else {
            // 값이 하나도 없으면 관련 키를 모두 제거하여 데이터를 깨끗하게 유지
            delete dataToSave.borderWidth;
            delete dataToSave.borderStyle;
            delete dataToSave.borderColor;
        }

        // 최종적으로 빈 값을 가진 키는 저장하지 않음 (이전과 동일)
        return Object.fromEntries(
            Object.entries(dataToSave).filter(([key, value]) => {
                if (value === null || value === undefined || value === '') return false;
                if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false;
                return true;
            })
        );
    }
}